import React, { FC, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { Global } from "./constants";
import { ConfigProvider, App as AntdApp } from "antd";
import en_US from 'antd/locale/en_US';

import AdminLayout from "./components/layout/adminLayout.component";
import ErrorBoundary from "./components/atoms/ErrorBoundary";
import VehicleTourProvider from "./components/organisms/my-vehicle/vehicle-tour.provider";

import "./scss/main.scss";
import "@syncfusion/ej2-react-inputs/styles/material.css";

import SuspenseWrapper from "./hoc/suspense.hoc";
import ProtectedRoute from "./hoc/protected-routes.hoc";

import NotFoundPage from "./pages/NotFound.page";
import lockBodyScrollOnSelect from "./hooks/lockBodyScrollOnSelect.hook";

// Función helper para crear componentes lazy con manejo de errores
const createLazyComponent = (importFn: () => Promise<any>, errorMessage = "Error al cargar la página") => {
  return React.lazy(() =>
    importFn().catch(() => ({
      default: () => (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <h2>{errorMessage}</h2>
          <button onClick={() => window.location.reload()}>
            Recargar página
          </button>
        </div>
      )
    }))
  );
};

const LoginPage = createLazyComponent(() => import("./pages/login.page"), "Error al cargar la página de login");
const HomePage = createLazyComponent(() => import("./pages/admin/home.page"));
const StorePage = createLazyComponent(() => import("./pages/admin/store.page"));
const SeminuevosPage = createLazyComponent(() => import("./pages/admin/seminuevos.page"));
const ProductsPage = createLazyComponent(() => import("./pages/admin/products.page"));
const HistoryPage = createLazyComponent(() => import("./pages/admin/history.page"));
const ConfigPage = createLazyComponent(() => import("./pages/admin/config-products.page"));
const SplitsPage = createLazyComponent(() => import("./pages/admin/splits.page"));
const SplitsProyectPage = createLazyComponent(() => import("./pages/admin/splits-proyect.page"));
const TransfersPage = createLazyComponent(() => import("./pages/admin/transfers.page"));
const CartaPortePage = createLazyComponent(() => import("./pages/admin/carta-porte.page"));
const SettingPage = createLazyComponent(() => import("./pages/admin/setting.page"));
const NotificationsPage = createLazyComponent(() => import("./pages/admin/notifications.page"));
const HistoryResponsablePage = createLazyComponent(() => import("./pages/admin/history-responsable.page"));
const ConfigPeticionesPage = createLazyComponent(() => import("./pages/admin/config-peticiones.page"));
const ConfigPeticionesApprovePage = createLazyComponent(() => import("./pages/admin/config-peticiones-approve.page"));
const ConfigPetitionsListPage = createLazyComponent(() => import("./pages/admin/config-petitions-list.page"));
const BitacorasPage = createLazyComponent(() => import("./pages/admin/bitacoras.page"));
const BitacoraNewPage = createLazyComponent(() => import("./pages/admin/bitacora-new.page"));
const BitacoraEditPage = createLazyComponent(() => import("./pages/admin/bitacora-edit.page"));
const BitacoraDetailPage = createLazyComponent(() => import("./pages/admin/bitacora-detail.page"));
const BitacorasConfigPage = createLazyComponent(() => import("./pages/admin/bitacoras-config.page"));
const BitacoraConfigNewPage = createLazyComponent(() => import("./pages/admin/bitacora-config-new.page"));
const BitacoraConfigEditPage = createLazyComponent(() => import("./pages/admin/bitacora-config-edit.page"));
const BitacoraConfigDetailPage = createLazyComponent(() => import("./pages/admin/bitacora-config-detail.page"));
const MyPickupsPage = createLazyComponent(() => import("./pages/admin/my-pickups.page"));
const MyDeliveriesPage = createLazyComponent(() => import("./pages/admin/my-deliveries.page"));
const MyVehiclePage = createLazyComponent(() => import("./pages/admin/my-vehicle.page"));
const ActivosPage = createLazyComponent(() => import("./pages/admin/activos.page"));
const MyVehicleFolioDetailPage = createLazyComponent(() => import("./pages/admin/my-vehicle-folio-detail.page"));
const GarantiasPage = createLazyComponent(() => import("./pages/admin/garantias.page"));
const HerramientasPage = createLazyComponent(() => import("./pages/admin/herramientas.page"));
const HerramientaNewPage = createLazyComponent(() => import("./pages/admin/herramienta-new.page"));
const HerramientaEditPage = createLazyComponent(() => import("./pages/admin/herramienta-edit.page"));
const KitNewPage = createLazyComponent(() => import("./pages/admin/kit-new.page"));
const KitEditPage = createLazyComponent(() => import("./pages/admin/kit-edit.page"));
const HerramientaEntradaPage = createLazyComponent(() => import("./pages/admin/herramienta-entrada.page"));
const HerramientasMovimientosPage = createLazyComponent(() => import("./pages/admin/herramientas-movimientos.page"));
const StockHerramientasPage = createLazyComponent(() => import("./pages/admin/stock-herramientas.page"));
const HerramientasPrestamosPage = createLazyComponent(() => import("./pages/admin/herramientas-prestamos.page"));
const HerramientasPrestamosNuevaPage = createLazyComponent(() => import("./pages/admin/herramientas-prestamos-nueva.page"));

type RootState = {
  auth: {
    login: boolean;
  };
};

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: state.auth.login,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const App: FC<PropsFromRedux> = ({ isLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const style = getComputedStyle(document.body);
  const defaultColor = style.getPropertyValue("--theme-color");

  lockBodyScrollOnSelect();

  useEffect(() => {
    const isUnprotected = Global.UNPROTECTED_ROUTES.some((path) =>
      location.pathname.startsWith(path)
    );

    if (!isLoggedIn && !isUnprotected) {
      navigate(Global.APP_URLS.LOGIN);
    }
  }, [isLoggedIn, location.pathname, navigate]);

  return (
    <ErrorBoundary>
      <div translate="no">
        <ConfigProvider
          locale={en_US}
          theme={{
            token: {
              colorPrimary: defaultColor,
            },
          }}
        >
          <AntdApp>
            <SuspenseWrapper>
              <ProtectedRoute route={location.pathname}>
                <Routes>
                  {/* AUTH */}
                  <Route path="/" element={<LoginPage />} />

                  {/* ADMIN layout */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="store" element={<StorePage />} />
                    <Route path="seminuevos" element={<SeminuevosPage />} />
                    <Route path="products" element={<ProductsPage />} />
                    <Route path="history" element={<HistoryPage />} />
                    <Route path="config" element={<ConfigPage />} />
                    <Route path="splits">
                      <Route index element={<SplitsPage />} />
                      <Route path=":projectId" element={<SplitsProyectPage />} />
                    </Route>
                    <Route path="transfers" element={<TransfersPage />} />
                    <Route path="carta-porte" element={<CartaPortePage />} />
                    <Route path="setting" element={<SettingPage />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="history-responsable" element={<HistoryResponsablePage />} />
                    <Route path="config-projects">
                      <Route index element={<ConfigPeticionesPage />} />
                      <Route path="approve" element={<ConfigPeticionesApprovePage />} />
                    </Route>
                    <Route path="config-petitions" element={<ConfigPetitionsListPage />} />
                    <Route path="garantias" element={<GarantiasPage />} />
                    <Route path="herramientas">
                      <Route index element={<HerramientasPage />} />
                      <Route path="nueva" element={<HerramientaNewPage />} />
                      <Route path="editar/:id" element={<HerramientaEditPage />} />
                      <Route path="entrada" element={<HerramientaEntradaPage />} />
                      <Route path="kits/nueva" element={<KitNewPage />} />
                      <Route path="kits/editar/:id" element={<KitEditPage />} />
                    </Route>
                    <Route path="herramientas-movimientos" element={<HerramientasMovimientosPage />} />
                    <Route path="herramientas-stock" element={<StockHerramientasPage />} />
                    <Route path="herramientas-prestamos">
                      <Route index element={<HerramientasPrestamosPage />} />
                      <Route path="nueva" element={<HerramientasPrestamosNuevaPage />} />
                    </Route>
                    <Route path="bitacoras">
                      <Route index element={<BitacorasPage />} />
                      <Route path="nueva" element={<BitacoraNewPage />} />
                      <Route path="editar/:id" element={<BitacoraEditPage />} />
                      <Route path="detalle/:id" element={<BitacoraDetailPage />} />
                    </Route>
                    <Route path="bitacoras-config">
                      <Route index element={<BitacorasConfigPage />} />
                      <Route path="nueva" element={<BitacoraConfigNewPage />} />
                      <Route path="editar/:id" element={<BitacoraConfigEditPage />} />
                      <Route path="detalle/:id" element={<BitacoraConfigDetailPage />} />
                    </Route>
                    <Route path="my-pickups" element={<MyPickupsPage />} />
                    <Route path="my-deliveries" element={<MyDeliveriesPage />} />
                    <Route path="my-vehicle" element={<VehicleTourProvider />}>
                      <Route index element={<MyVehiclePage />} />
                      <Route path="folio/:folioId" element={<MyVehicleFolioDetailPage />} />
                    </Route>
                    <Route path="activos" element={<ActivosPage />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ProtectedRoute>
            </SuspenseWrapper>
          </AntdApp>

        </ConfigProvider>
      </div>
    </ErrorBoundary>
  );
};

export default connector(App);
